import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { ItemSourceGuesserModel } from 'models/ItemSourceGuesserModel';
import { IItemSourceGuesserCreate } from 'types/itemSourceGuesser';
import DialectSelect, { IDialectOption } from 'components/selects/DialectSelect';
import KLineSelectorList from 'components/KLineSelectorList';
import { SelectLayout } from 'components/selects/SelectLayout';

interface IProps {
  model: ItemSourceGuesserModel;
  onClose: () => void;
  onSuccess: (jobId: number) => void;
}

type SourceFilterCode = 'all' | 'empty_only' | 'non_empty_only';
const sourceFilterOptions: { code: SourceFilterCode; label: string }[] = [
  { code: 'all', label: 'All' },
  { code: 'empty_only', label: 'Empty Only' },
  { code: 'non_empty_only', label: 'Non-Empty Only' },
];

const ItemSourceGuesserCreate: React.FC<IProps> = ({ model, onClose, onSuccess }) => {
  const [name, setName] = useState<string>('');
  const [dialectId, setDialectId] = useState<number | ''>('');
  const [sourceFilter, setSourceFilter] = useState<'all' | 'empty_only' | 'non_empty_only'>('all');
  const [klineIds, setKlineIds] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDialectChange = useCallback((dialect: IDialectOption) => {
    setDialectId(dialect.dialectId);
  }, []);

  const handleCreate = async () => {
    setError(null);
    if (!name.trim()) {
      setError('Please enter a name');
      return;
    }
    if (!dialectId) {
      setError('Please select a dialect');
      return;
    }
    setSaving(true);
    try {
      const payload: IItemSourceGuesserCreate = {
        name: name.trim(),
        dialect_id: Number(dialectId),
        source_filter: sourceFilter,
        filter_kline_ids: klineIds.length ? klineIds : undefined,
      };
      const res = await model.createJob(payload);
      if (res.data?.id) {
        onSuccess(res.data.id);
      }
    } catch (e) {
      setError('Failed to create job');
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container>
      <Header>
        <Title>Create Item Source Guesser Job</Title>
        <CloseButton onClick={onClose}>×</CloseButton>
      </Header>
      <Content>
        <Section>
          <SectionTitle>Name</SectionTitle>
          <NameInput
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter a descriptive name for this job"
            autoFocus
          />
        </Section>
        <Section>
          <SectionTitle>Dialect</SectionTitle>
          <DialectSelect handleDialectChange={handleDialectChange} onlySource />
        </Section>
        <Section>
          <SectionTitle>Source Filter</SectionTitle>
          <SourceFilterSelect
            data={sourceFilterOptions}
            onChange={(opt: any) => setSourceFilter(opt?.code)}
            label="Source Filter"
            optionLabelKey="label"
            optionValueKey="code"
            currentValue={sourceFilter}
          />
        </Section>
        <Section>
          <SectionTitle>KLines (optional)</SectionTitle>
          <KLineSelectorList value={klineIds} onChange={setKlineIds} />
        </Section>
        {error && <ErrorText>{error}</ErrorText>}
      </Content>
      <Footer>
        <CancelButton onClick={onClose}>Cancel</CancelButton>
        <CreateButton onClick={handleCreate} disabled={saving}>
          {saving ? 'Creating...' : 'Create'}
        </CreateButton>
      </Footer>
    </Container>
  );
};

export default ItemSourceGuesserCreate;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: white;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 24px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 30px;
  cursor: pointer;
  color: #666;

  &:hover {
    color: #333;
  }
`;

const Content = styled.div`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
`;

const Section = styled.div`
  margin-bottom: 20px;
`;

const SectionTitle = styled.h3`
  margin-bottom: 10px;
  font-size: 16px;
  color: #333;
`;

const ErrorText = styled.div`
  color: #dc3545;
  font-size: 14px;
  margin-top: 5px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 20px;
  border-top: 1px solid #e0e0e0;
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  background-color: #f0f0f0;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background-color: #e0e0e0;
  }
`;

const CreateButton = styled.button`
  padding: 10px 20px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:hover:not(:disabled) {
    background-color: #0056b3;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SourceFilterSelect = styled(SelectLayout)`
  width: 200px;
  flex-grow: 0;
`;

const NameInput = styled.input`
  width: 94%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.1);
  }
`;
